import { useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import useUser from "../../Hook/user";
import { ADS_BANNERS, AdsBannerVariant } from "./config";

const pickRandom = <T,>(items: T[]) =>
  items[Math.floor(Math.random() * items.length)];

const isPathBanned = (currentPath: string, bannedList?: string[]) => {
  if (!bannedList || bannedList.length === 0) return false;
  return bannedList.some((path) =>
    currentPath === path || currentPath.startsWith(`${path}/`),
  );
};

const isPathAllowed = (currentPath: string, allowedList?: string[]) => {
  if (!allowedList || allowedList.length === 0) return true;
  return allowedList.some((path) =>
    currentPath === path || currentPath.startsWith(`${path}/`),
  );
};

type AdsBannersProps = {
  variant?: AdsBannerVariant;
  slot?: string;
};

function AdsBanners({ variant, slot }: AdsBannersProps) {
  const location = useLocation();
  const user = useUser();
  const selectedRef = useRef<AdsBannerVariant | null>(null);
  const lastPathRef = useRef(location.pathname);

  const isEligible =
    user?.informations?.gender === "man" &&
    user?.informations?.is_subscribed === false &&
    ADS_BANNERS.length > 0;

  const selectedBanner = useMemo(() => {
    if (!isEligible) return null;

    const candidates = ADS_BANNERS.filter((banner) => {
      if (variant && banner.id !== variant) return false;
      if (slot && banner.slot && banner.slot !== slot) return false;
      if (!isPathAllowed(location.pathname, banner.allowedPages)) return false;
      return !isPathBanned(location.pathname, banner.bannedFromPages);
    });

    if (!candidates.length) return null;

    if (lastPathRef.current !== location.pathname) {
      selectedRef.current = null;
      lastPathRef.current = location.pathname;
    }

    if (selectedRef.current) {
      return candidates.find((banner) => banner.id === selectedRef.current) ??
        candidates[0];
    }

    const picked = pickRandom(candidates);
    selectedRef.current = picked.id;
    return picked;
  }, [isEligible, location.pathname, slot, variant]);

  if (!selectedBanner) return null;
  const BannerComponent = selectedBanner.Component;
  return <BannerComponent />;
}

export default AdsBanners;
